/**
 * SignaturesApprovals — "التواقيع والاعتماد" (iter-217c-v17)
 *
 * Landing hub for admin signature-related workflows. Each tile links to a
 * specific approval queue. Currently:
 *   • Certificate approvals (شهادات لمن يهمه الأمر + تهنئة)
 *   • Signature settings shortcut
 */
import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { PenLine, Award, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';

import DashboardLayout from '../components/DashboardLayout';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function SignaturesApprovals() {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getAuthHeaders: authHeaders } = useAuth();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const Arrow = isAr ? ChevronLeft : ChevronRight;

  const [pendingCount, setPendingCount] = useState(0);

  const fetchPending = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${BACKEND_URL}/api/admin/certificates/list`,
        { headers: authHeaders(), params: { status: 'pending_approval' } },
      );
      setPendingCount(Array.isArray(data?.data) ? data.data.length : 0);
    } catch {
      /* silent */
    }
  }, [authHeaders]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const TILES = [
    {
      id: 'cert-approvals',
      testid: 'tile-cert-approvals',
      route: '/signatures-approvals/certificates',
      icon: Award,
      color: '#F5A55A',
      titleAr: 'اعتماد شهادات لمن يهمهم الأمر',
      titleEn: 'Certificate approvals',
      subAr:   `شهادات معلّقة تحتاج اعتمادك — ${pendingCount}`,
      subEn:   `Pending certificates awaiting your approval — ${pendingCount}`,
      badge:   pendingCount,
    },
    // iter-218 (Feb 2026) — "إعدادات التوقيع والختم" moved out of the
    // Employees dashboard to لوحة التحكم ▸ التحكم بإدارة الشركة ▸
    // إعدادات الموظفين. Only the primary admin should manage this.
  ];

  return (
    <DashboardLayout
      title="التواقيع والاعتماد"
      titleEn="Signatures & Approvals"
      subtitle="إدارة التواقيع واعتماد المستندات"
      subtitleEn="Manage signatures and document approvals"
      screenCode="EMP-D02"
      backPath="/employee-dashboard"
      onBack={() => navigate('/employee-dashboard')}
      breadcrumb={[
        { labelAr: 'لوحة تحكم الموظف والمشرف', labelEn: 'Employee & Supervisor Dashboard', path: '/employee-dashboard' },
        { labelAr: 'التواقيع والاعتماد', labelEn: 'Signatures & Approvals' },
      ]}
    >
      <section
        className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6"
        data-testid="signatures-approvals-page"
      >
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {TILES.map((tile, i) => {
            const IconC = tile.icon;
            return (
              <motion.button
                key={tile.id}
                type="button"
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
                whileHover={{ y: -4 }}
                onClick={() => navigate(tile.route)}
                className="relative group bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border transition-all text-start"
                style={{ borderColor: `${tile.color}30` }}
                data-testid={tile.testid}
              >
                <div
                  className="w-14 h-14 rounded-xl flex items-center justify-center border transition-transform group-hover:scale-110 mb-3"
                  style={{ backgroundColor: `${tile.color}15`, borderColor: `${tile.color}40` }}
                >
                  <IconC className="h-7 w-7" style={{ color: tile.color }} />
                </div>
                <h3 className="text-base font-bold text-white mb-1 flex items-center gap-2">
                  {isAr ? tile.titleAr : tile.titleEn}
                  <Arrow className="h-4 w-4 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: tile.color }} />
                </h3>
                <p className="text-xs text-white/60">
                  {isAr ? tile.subAr : tile.subEn}
                </p>
                {tile.badge > 0 && (
                  <span
                    className="absolute top-4 end-4 inline-flex items-center justify-center min-w-[26px] h-6 px-2 rounded-full text-[11px] font-bold text-white shadow-md"
                    style={{ backgroundColor: tile.color }}
                    data-testid={`${tile.testid}-badge`}
                  >
                    {tile.badge}
                  </span>
                )}
              </motion.button>
            );
          })}
        </div>
      </section>
    </DashboardLayout>
  );
}
